"use client"

import { ArrowLeft, FileText, Shield, Scale, AlertTriangle, CreditCard, Globe, Clock, Mail, Zap, Lightbulb } from "lucide-react"
import Link from "next/link"

export default function TermsOfServicePage() {
  const lastUpdated = "March 1, 2026"
  const effectiveDate = "March 1, 2026"

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border dark:border-white/[0.06] bg-background/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/auth/signin" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Sign In</span>
          </Link>
          <div className="flex items-center gap-1">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/favicon.svg" alt="FluxPay" className="w-6 h-6" />
            <span className="font-semibold tracking-wide uppercase text-sm hidden dark:inline" style={{ background: 'linear-gradient(135deg, #e0f4ff 0%, #ffffff 25%, #b0d8ff 50%, #ffffff 75%, #90b0e0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
            <span className="font-semibold tracking-wide uppercase text-sm inline dark:hidden" style={{ background: 'linear-gradient(135deg, #0e7490 0%, #155e75 25%, #1D3557 50%, #0891b2 75%, #164e63 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
              <FileText className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Terms of Service</h1>
              <p className="text-muted-foreground text-sm">FluxPay Payment Services Agreement</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>Last Updated: {lastUpdated}</span>
            <span>|</span>
            <span>Effective: {effectiveDate}</span>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="mb-12 p-6 rounded-2xl bg-card/80 dark:bg-white/[0.02] border border-border dark:border-white/[0.06]">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Quick Navigation</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { label: "Agreement Overview", href: "#overview" },
              { label: "Account Terms", href: "#account" },
              { label: "Payment Services", href: "#services" },
              { label: "Fees & Billing", href: "#fees" },
              { label: "Prohibited Activities", href: "#prohibited" },
              { label: "Data Protection", href: "#data" },
              { label: "Liability", href: "#liability" },
              { label: "Termination", href: "#termination" },
              { label: "Governing Law", href: "#law" },
              { label: "Intellectual Property", href: "#ip" },
              { label: "Force Majeure", href: "#force-majeure" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="px-4 py-2 rounded-lg bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.06] text-sm text-muted-foreground hover:text-foreground hover:border-cyan-500/30 transition-all"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>

        {/* Legal Content */}
        <div className="prose prose-invert prose-gray max-w-none">
          {/* Section 1 */}
          <section id="overview" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-cyan-400" />
              1. Agreement Overview
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                These Terms of Service ("Agreement") constitute a legally binding contract between you ("Merchant," "you," or "your") and FluxPay, a trade name of Ideation House Financial Services Limited (CBCA Corporation No. 1419582-5, FINTRAC MSB Registration No. M22512329) ("FluxPay," "we," "us," or "our"), governing your access to and use of FluxPay's payment processing services, APIs, dashboard, and related technologies (collectively, the "Services").
              </p>
              <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                <p className="text-cyan-200 text-sm">
                  <strong>MSB Verification:</strong> You may verify our registration on the FINTRAC MSB Registry at{" "}
                  <a href="https://www10.fintrac-canafe.gc.ca/msb-esm/public/detailed-information/bm10/7cbe3c6bded2413e9bff2e7c3e1c9fa1" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline hover:text-cyan-300">
                    FINTRAC Public MSB Registry
                  </a>.
                </p>
              </div>
              <p>
                By accessing or using our Services, creating an account, or clicking "I Agree," you acknowledge that you have read, understood, and agree to be bound by this Agreement, our Privacy Policy, and any additional terms and policies referenced herein.
              </p>
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <p className="text-amber-200 text-sm">
                  <strong>Important:</strong> If you do not agree to these terms, you must not access or use our Services. These terms contain provisions that limit our liability and require arbitration for dispute resolution.
                </p>
              </div>
              <p>
                FluxPay reserves the right to modify this Agreement at any time. We will notify you of material changes via email or through the Services at least thirty (30) days before such changes take effect. Your continued use of the Services after the effective date constitutes acceptance of the modified terms.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section id="account" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-400" />
              2. Account Registration and Eligibility
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">2.1 Eligibility Requirements</h3>
              <p>To use our Services, you must:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Be at least 18 years of age or the age of legal majority in your jurisdiction</li>
                <li>Be a legally registered business entity in good standing in your jurisdiction of incorporation</li>
                <li>Have the legal authority to bind your organization to this Agreement</li>
                <li>Not be located in, or a resident of, any country subject to comprehensive sanctions by the United States, European Union, or United Nations</li>
                <li>Not be listed on any government-maintained list of prohibited or restricted parties</li>
                <li>Comply with all applicable laws, regulations, and card network rules</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">2.2 Account Registration</h3>
              <p>
                When registering for an account, you agree to provide accurate, current, and complete information about yourself and your business. You must promptly update this information if it changes. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">2.3 Verification Requirements</h3>
              <p>
                FluxPay may require you to provide additional documentation to verify your identity, business legitimacy, and compliance status. This may include, but is not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Government-issued identification for beneficial owners</li>
                <li>Business registration documents and certificates of incorporation</li>
                <li>Proof of address and banking information</li>
                <li>Financial statements and tax documentation</li>
                <li>Website URL and business description</li>
              </ul>
              <p>
                Failure to provide requested documentation within the specified timeframe may result in account suspension or termination.
              </p>
            </div>
          </section>

          {/* Section 3 */}
          <section id="services" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-cyan-400" />
              3. Payment Processing Services
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">3.1 Service Description</h3>
              <p>
                FluxPay provides payment processing services that enable you to accept payments from your customers via credit cards, debit cards, bank transfers, and other payment methods. Our Services include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Payment gateway and processing infrastructure</li>
                <li>Intelligent payment routing optimization</li>
                <li>Fraud detection and prevention tools</li>
                <li>Merchant dashboard and reporting analytics</li>
                <li>API access for custom integrations</li>
                <li>Settlement and payout services</li>
                <li>Chargeback management and dispute resolution</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">3.2 Payment Network Rules</h3>
              <p>
                Your use of the Services is subject to the rules, regulations, and guidelines established by payment networks including Visa, Mastercard, American Express, and other applicable card networks ("Network Rules"). You agree to comply with all applicable Network Rules, which may be modified from time to time.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">3.3 Settlement and Payouts</h3>
              <p>
                Subject to the terms of this Agreement and successful completion of our verification procedures, FluxPay will settle funds to your designated bank account according to the payout schedule specified in your merchant agreement. Standard settlement periods range from T+1 to T+7 business days, depending on your account type, transaction history, and risk assessment.
              </p>
              <p>
                FluxPay reserves the right to hold, delay, or withhold payouts in cases of suspected fraud, excessive chargebacks, violation of this Agreement, or as required by law or Network Rules. Specifically, FluxPay may initiate a settlement hold under the following conditions:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Chargeback ratio exceeds 1% of monthly transactions</li>
                <li>Suspected fraud or unauthorized activity</li>
                <li>Regulatory investigation or compliance review</li>
                <li>Merchant&apos;s processing volume exceeds approved limits</li>
                <li>Outstanding fees or penalties owed to FluxPay</li>
              </ul>
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <p className="text-amber-200 text-sm">
                  <strong>Notice:</strong> FluxPay will provide written notice within 2 business days of initiating a settlement hold, including the reason for the hold and estimated duration.
                </p>
              </div>

              <h3 className="text-lg font-medium text-foreground mt-6">3.4 Reserves</h3>
              <p>
                FluxPay may establish a reserve account to cover potential chargebacks, refunds, or other liabilities. The reserve amount and terms will be communicated to you and may be adjusted based on your transaction history and risk profile.
              </p>

              <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                <p className="text-cyan-200 text-sm">
                  <strong>Electronic Fund Transfer Disclosure:</strong> As a registered Money Services Business, FluxPay provides electronic fund transfer services subject to the Proceeds of Crime (Money Laundering) and Terrorist Financing Act. Merchants and their customers have the right to dispute unauthorized electronic fund transfers and seek remediation through our support channels within 90 days of the transaction.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section id="fees" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-cyan-400" />
              4. Fees, Billing, and Taxes
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">4.1 Fee Structure</h3>
              <p>
                You agree to pay all fees associated with your use of the Services as set forth in your merchant pricing agreement. Fees may include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Transaction Fees:</strong> A percentage of each transaction amount plus a fixed per-transaction fee</li>
                <li><strong>Monthly Fees:</strong> Fixed monthly service fees, if applicable</li>
                <li><strong>Chargeback Fees:</strong> Fees assessed for each chargeback received</li>
                <li><strong>Refund Fees:</strong> Processing fees for refund transactions</li>
                <li><strong>Currency Conversion Fees:</strong> Fees for multi-currency transactions</li>
                <li><strong>Payout Fees:</strong> Fees for bank transfers and settlements</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">4.2 Fee Changes</h3>
              <p>
                FluxPay may modify its fee structure with thirty (30) days advance written notice. Continued use of the Services after the effective date of fee changes constitutes acceptance of the new fees.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">4.3 Taxes</h3>
              <p>
                You are responsible for determining and paying all taxes applicable to your use of the Services and the transactions processed through your account. FluxPay's fees are exclusive of all taxes, levies, or duties imposed by taxing authorities, and you shall be responsible for payment of all such taxes.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section id="prohibited" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-cyan-400" />
              5. Prohibited Activities and Restricted Businesses
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">5.1 Prohibited Uses</h3>
              <p>You may not use the Services to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violate any applicable law, regulation, or Network Rules</li>
                <li>Process transactions for illegal goods or services</li>
                <li>Engage in money laundering, terrorist financing, or other financial crimes</li>
                <li>Process transactions without proper customer authorization</li>
                <li>Submit fraudulent or unauthorized transactions</li>
                <li>Manipulate or abuse our pricing, fees, or promotional offers</li>
                <li>Circumvent any security measures or access controls</li>
                <li>Interfere with or disrupt the integrity of the Services</li>
                <li>Use the Services for any purpose other than legitimate business transactions</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">5.2 Restricted Business Categories</h3>
              <p>
                The following business categories are prohibited or restricted from using FluxPay's Services. This list is non-exhaustive and FluxPay reserves the right to decline service to any business at its sole discretion:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Adult content and services</li>
                <li>Gambling and betting (unless properly licensed)</li>
                <li>Cryptocurrency exchanges and ICOs</li>
                <li>Weapons, ammunition, and explosives</li>
                <li>Controlled substances and drug paraphernalia</li>
                <li>Counterfeit or unauthorized goods</li>
                <li>Multi-level marketing, pyramid schemes, and Ponzi schemes</li>
                <li>Debt collection agencies (unlicensed)</li>
                <li>Credit repair and debt settlement services</li>
                <li>Telemarketing and outbound call centers</li>
                <li>Timeshares and vacation clubs</li>
                <li>Binary options and forex/CFD trading platforms</li>
                <li>Pharmaceuticals and nutraceuticals</li>
                <li>Travel agencies (without IATA accreditation)</li>
                <li>Subscription traps and negative option billing</li>
                <li>CBD/hemp products (where not legally licensed)</li>
                <li>Bail bonds</li>
                <li>Firearms and ammunition accessories</li>
                <li>Dating services (non-verified)</li>
                <li>Penny auctions</li>
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section id="data" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-400" />
              6. Data Protection and Security
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">6.1 Security Standards</h3>
              <p>
                FluxPay implements industry-standard security practices to protect cardholder data. You agree to comply with all applicable payment card industry requirements and to never store, process, or transmit cardholder data in an insecure manner.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">6.2 Data Processing</h3>
              <p>
                To provide the Services, FluxPay processes personal data on your behalf. Our data processing practices are governed by our Privacy Policy and applicable data protection laws including the General Data Protection Regulation (GDPR), the Personal Information Protection and Electronic Documents Act (PIPEDA), and other applicable privacy legislation. You represent that you have obtained all necessary consents and authorizations for us to process data as described in this Agreement and our Privacy Policy.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">6.3 Security Measures</h3>
              <p>
                You agree to implement and maintain reasonable security measures to protect cardholder data and prevent unauthorized access to your account. This includes, but is not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Using strong, unique passwords and enabling multi-factor authentication</li>
                <li>Maintaining up-to-date security software and systems</li>
                <li>Training employees on data security best practices</li>
                <li>Promptly reporting any suspected security breaches to FluxPay</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">6.4 Compliance Officer</h3>
              <p>
                FluxPay maintains a designated Compliance Officer responsible for overseeing AML/KYC compliance and data protection. Compliance inquiries may be directed to{" "}
                <a href="mailto:compliance@fluxpay.online" className="text-cyan-400 underline hover:text-cyan-300">compliance@fluxpay.online</a>.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section id="liability" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-cyan-400" />
              7. Limitation of Liability
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">7.1 Disclaimer of Warranties</h3>
              <p>
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. FLUXPAY DOES NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">7.2 Limitation of Liability</h3>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL FLUXPAY, ITS AFFILIATES, DIRECTORS, OFFICERS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your access to or use of or inability to access or use the Services</li>
                <li>Any conduct or content of any third party on the Services</li>
                <li>Any content obtained from the Services</li>
                <li>Unauthorized access, use, or alteration of your transmissions or content</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">7.3 Cap on Liability</h3>
              <p>
                FLUXPAY'S TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING OUT OF OR RELATING TO THIS AGREEMENT OR THE SERVICES SHALL NOT EXCEED THE GREATER OF (A) THE FEES PAID BY YOU TO FLUXPAY DURING THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED US DOLLARS ($100).
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">7.4 Indemnification</h3>
              <p>
                You agree to indemnify, defend, and hold harmless FluxPay and its affiliates, officers, directors, employees, and agents from and against any and all claims, damages, obligations, losses, liabilities, costs, and expenses arising from: (a) your use of the Services; (b) your violation of this Agreement; (c) your violation of any third-party rights; or (d) any dispute between you and your customers.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section id="termination" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-400" />
              8. Term and Termination
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">8.1 Term</h3>
              <p>
                This Agreement begins on the date you accept it and continues until terminated by either party in accordance with this section.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">8.2 Termination by You</h3>
              <p>
                You may terminate this Agreement at any time by providing written notice to FluxPay and ceasing all use of the Services. Termination will be effective upon the completion of all pending transactions and settlement of any outstanding obligations.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">8.3 Termination by FluxPay</h3>
              <p>
                FluxPay may suspend or terminate your access to the Services immediately and without notice if:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You breach any provision of this Agreement</li>
                <li>You engage in fraudulent or illegal activity</li>
                <li>Your chargeback rate exceeds acceptable thresholds</li>
                <li>We are required to do so by law or Network Rules</li>
                <li>We determine that your use of the Services poses a risk to FluxPay or third parties</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">8.4 Effect of Termination</h3>
              <p>
                Upon termination, your right to use the Services will immediately cease. FluxPay will complete the settlement of any pending transactions, less any amounts owed to FluxPay or held in reserve. Provisions of this Agreement that by their nature should survive termination will survive, including but not limited to indemnification, limitation of liability, and dispute resolution provisions.
              </p>
            </div>
          </section>

          {/* Section 9 */}
          <section id="law" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-cyan-400" />
              9. Governing Law and Dispute Resolution
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">9.1 Governing Law</h3>
              <p>
                This Agreement shall be governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein, without regard to conflict of law provisions.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">9.2 Arbitration</h3>
              <p>
                Any dispute, controversy, or claim arising out of or relating to this Agreement, or the breach, termination, or validity thereof, shall be settled by binding arbitration in accordance with the rules of the ADR Institute of Canada. The arbitration shall take place in Ontario, Canada, and shall be conducted in English.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">9.3 Class Action Waiver</h3>
              <p>
                YOU AND FLUXPAY AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">9.4 Equitable Relief</h3>
              <p>
                Notwithstanding the foregoing, either party may seek injunctive or other equitable relief in any court of competent jurisdiction to protect its intellectual property rights or confidential information.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section id="ip" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-cyan-400" />
              10. Intellectual Property
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">10.1 FluxPay IP Rights</h3>
              <p>
                FluxPay retains all intellectual property rights to the platform, APIs, documentation, software, trademarks, and related technology. Nothing in this Agreement transfers or assigns any ownership rights to the Merchant.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">10.2 Merchant Data Ownership</h3>
              <p>
                The Merchant retains ownership of their business data and transaction records. FluxPay is granted a limited license to use such data solely for the purpose of providing the Services and as described in our Privacy Policy.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">10.3 License to Use Services</h3>
              <p>
                Subject to the terms of this Agreement, FluxPay grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Services during the term of this Agreement solely for your legitimate business purposes.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">10.4 Trademark Usage</h3>
              <p>
                Neither party may use the other party&apos;s trademarks, logos, or trade names without the prior written consent of the other party. Any authorized use must comply with the trademark owner&apos;s brand guidelines.
              </p>
            </div>
          </section>

          {/* Section 11 */}
          <section id="force-majeure" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-cyan-400" />
              11. Force Majeure
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Neither party shall be liable for any failure or delay in performing its obligations under this Agreement due to circumstances beyond its reasonable control, including but not limited to natural disasters, war, terrorism, pandemics, epidemics, government actions or orders, power failures, internet or telecommunications disruptions, payment network outages, acts of civil or military authorities, fire, floods, accidents, strikes, or shortages of transportation, facilities, fuel, energy, labor, or materials.
              </p>
              <p>
                The affected party must notify the other party in writing within forty-eight (48) hours of becoming aware of the force majeure event and must use reasonable efforts to mitigate the impact and resume performance as soon as practicable.
              </p>
              <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                <p className="text-cyan-200 text-sm">
                  <strong>Note:</strong> If a force majeure event continues for more than ninety (90) consecutive days, either party may terminate this Agreement by providing written notice to the other party.
                </p>
              </div>
            </div>
          </section>

          {/* Section 12 */}
          <section id="general" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4">12. General Provisions</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">12.1 Entire Agreement</h3>
              <p>
                This Agreement, together with the Privacy Policy and any other policies referenced herein, constitutes the entire agreement between you and FluxPay regarding the Services and supersedes all prior agreements and understandings.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">12.2 Severability</h3>
              <p>
                If any provision of this Agreement is held to be invalid or unenforceable, such provision shall be struck and the remaining provisions shall be enforced to the fullest extent under law.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">12.3 Assignment</h3>
              <p>
                You may not assign or transfer this Agreement or any rights or obligations hereunder without the prior written consent of FluxPay. FluxPay may assign this Agreement without restriction.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">12.4 No Waiver</h3>
              <p>
                The failure of FluxPay to enforce any right or provision of this Agreement shall not constitute a waiver of such right or provision.
              </p>

            </div>
          </section>

          {/* Contact Section */}
          <section className="mb-12 p-6 rounded-2xl bg-card/80 dark:bg-white/[0.02] border border-border dark:border-white/[0.06]">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-cyan-400" />
              Contact Information
            </h2>
            <div className="space-y-3 text-muted-foreground">
              <p>For questions about these Terms of Service, please contact us:</p>
              <div className="space-y-2">
                <p><strong>FluxPay</strong></p>
                <p>Ideation House Financial Services Limited</p>
                <p>3080 Yonge Street, Suite 6060, Toronto, Ontario, M4N 3N1, Canada</p>
                <p>Legal Department</p>
                <p>Email: legal@fluxpay.online</p>
                <p>Support: support@fluxpay.online</p>
              </div>
            </div>
          </section>
        </div>

        {/* Footer Navigation */}
        <div className="pt-8 border-t border-border dark:border-white/[0.06]">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <Link href="/privacy" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm">
              Privacy Policy
            </Link>
            <span className="text-gray-600">·</span>
            <Link href="/aml-policy" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm">
              AML/KYC Policy
            </Link>
            <span className="text-gray-600">·</span>
            <Link href="/refund-policy" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm">
              Refund Policy
            </Link>
          </div>
          <div className="flex justify-end">
            <Link href="/auth/signin" className="px-6 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20 transition-all">
              Back to Sign In
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}
